import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../App';
import { 
  BookOpen, Crown, Check, X, Zap, Star, Shield, 
  Download, Brain, Users, MessageSquare, ArrowLeft,
  Sparkles, CheckCircle2, Gift
} from 'lucide-react';

const PLANS = [
  {
    id: 'free',
    name: 'Ücretsiz',
    price: 0,
    description: 'Temel mizaç testi',
    features: [
      { name: 'Mizaç Testi', included: true },
      { name: '1 Ücretsiz Rapor', included: true },
      { name: 'Temel Analiz', included: true },
      { name: 'PDF İndirme', included: false },
      { name: 'Detaylı Rapor', included: false },
      { name: 'AI Mizaç Koçu', included: false },
      { name: 'Sınırsız Rapor', included: false },
      { name: 'Öncelikli Destek', included: false }
    ],
    popular: false,
    current: true
  },
  {
    id: 'individual',
    name: 'Bireysel',
    price: 99,
    period: '/ay',
    description: 'Kendinizi derinlemesine keşfedin',
    features: [
      { name: 'Mizaç Testi', included: true },
      { name: 'Sınırsız Rapor', included: true },
      { name: 'Detaylı Analiz', included: true },
      { name: 'PDF İndirme', included: true },
      { name: 'Gelişmiş Rapor', included: true },
      { name: 'AI Mizaç Koçu', included: true },
      { name: '5 Aile Üyesi', included: true },
      { name: 'Email Destek', included: true }
    ],
    popular: true,
    current: false
  },
  {
    id: 'professional',
    name: 'Profesyonel',
    price: 299,
    period: '/ay',
    description: 'Danışmanlar ve eğitimciler için',
    features: [
      { name: 'Tüm Bireysel Özellikler', included: true },
      { name: 'Sınırsız Rapor', included: true },
      { name: '50 Öğrenci/Danışan', included: true },
      { name: 'Toplu Değerlendirme', included: true },
      { name: 'Sınıf Analizi', included: true },
      { name: 'Profesyonel Raporlar', included: true },
      { name: 'API Erişimi', included: true },
      { name: 'Öncelikli Destek', included: true }
    ],
    popular: false,
    current: false
  }
];

export default function UpgradePage({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('free');

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/subscription/status');
      setCurrentPlan(response.data.plan?.tier || 'free');
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const handleUpgrade = async (planId) => {
    if (planId === 'free') {
      toast.info('Zaten ücretsiz plandısınız');
      return;
    }

    setLoading(true);
    try {
      // In a real app, this would redirect to Stripe checkout
      toast.info('Ödeme sistemi yakında aktif olacak. Lütfen bizimle iletişime geçin.');
      // const response = await api.post('/subscription/checkout', { plan: planId });
      // window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri Dön
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
          </div>
          <div className="w-24"></div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 mb-4 px-4 py-2">
            <Crown className="w-4 h-4 mr-2" />
            Premium Planlar
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Kendinizi Daha İyi Tanıyın
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Sınırsız rapor, AI koçluk ve detaylı analizlerle mizaç yolculuğunuza devam edin
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {PLANS.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative shadow-xl border-2 transition-all hover:scale-105 ${
                plan.popular 
                  ? 'border-purple-400 bg-gradient-to-br from-purple-50 to-blue-50' 
                  : plan.id === currentPlan
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-slate-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    En Popüler
                  </Badge>
                </div>
              )}
              {plan.id === currentPlan && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-emerald-500 text-white px-4 py-1">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Mevcut Plan
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pt-8">
                <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                  plan.popular 
                    ? 'bg-gradient-to-br from-purple-500 to-blue-500' 
                    : plan.id === 'professional'
                      ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                      : 'bg-slate-200'
                }`}>
                  {plan.id === 'free' && <Gift className="w-7 h-7 text-slate-600" />}
                  {plan.id === 'individual' && <Crown className="w-7 h-7 text-white" />}
                  {plan.id === 'professional' && <Shield className="w-7 h-7 text-white" />}
                </div>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-800">₺{plan.price}</span>
                  {plan.period && <span className="text-slate-500">{plan.period}</span>}
                </div>
              </CardHeader>
              
              <CardContent className="pt-4">
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
                      )}
                      <span className={feature.included ? 'text-slate-700' : 'text-slate-400'}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full py-6 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600'
                      : plan.id === currentPlan
                        ? 'bg-slate-300 cursor-not-allowed'
                        : 'bg-slate-800 hover:bg-slate-700'
                  }`}
                  disabled={plan.id === currentPlan || loading}
                  onClick={() => handleUpgrade(plan.id)}
                >
                  {plan.id === currentPlan ? (
                    'Mevcut Planınız'
                  ) : plan.id === 'free' ? (
                    'Ücretsiz'
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      {plan.popular ? 'Premium\'a Geç' : 'Planı Seç'}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features Comparison */}
        <Card className="shadow-xl border-none mb-12">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Neden Premium?</CardTitle>
            <CardDescription>Ücretsiz ve premium planlar arasındaki farklar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">AI Mizaç Koçu</h3>
                <p className="text-slate-600 text-sm">
                  GPT-5.2 destekli kişiselleştirilmiş koçluk ile mizaç tipinize özel rehberlik alın
                </p>
              </div>
              
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50">
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Detaylı Raporlar</h3>
                <p className="text-slate-600 text-sm">
                  Temel raporun ötesinde, kariyer önerileri, ilişki dinamikleri ve gelişim alanları
                </p>
              </div>
              
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Aile & Grup</h3>
                <p className="text-slate-600 text-sm">
                  Aile üyelerinizi ekleyin, uyum analizleri yapın ve birlikte gelişin
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <div className="text-center">
          <p className="text-slate-500 mb-4">Sorularınız mı var?</p>
          <Button variant="outline" onClick={() => navigate('/contact')}>
            <MessageSquare className="w-4 h-4 mr-2" />
            Bize Ulaşın
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>© 2025 Mizaç360. Tüm hakları saklıdır.</p>
        </div>
      </footer>
    </div>
  );
}
